import {
  BookOpen,
  Building2,
  CalendarClock,
  ChartColumn,
  ClipboardCheck,
  ClipboardList,
  Clock,
  CircleDollarSign,
  Cylinder,
  Droplet,
  FileText,
  Fuel,
  History,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  ListPlus,
  ListTodo,
  Package,
  Plus,
  Printer,
  QrCode,
  Receipt,
  Scale,
  Settings2,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Timer,
  Truck,
  Upload,
} from 'lucide-react';
import type { ComponentType } from 'react';

import type { CaminhoConstruido, IdModulo } from '@/modulos/acesso/sprint';

/**
 * O QUE O PAINEL DA NAVEGAÇÃO MOSTRA DE CADA TELA — grupo, ícone e descrição.
 *
 * Vem do desenho que o dono entregou (`sidebar.zip`, "Navegação Lateral"): o
 * trilho tem os módulos, e o painel que abre ao lado mostra as telas daquele
 * módulo **agrupadas e explicadas**, uma linha de descrição por tela. A
 * navegação anterior listava só o nome — e nome de tela sozinho não diz a quem
 * usa o sistema uma vez por semana o que ele vai encontrar lá dentro.
 *
 * ## Por que uma tabela à parte, e exaustiva
 *
 * A lista de telas de cada módulo já mora em `Navegacao.tsx` (`SECOES`), com a
 * licença de cada uma, e é ela que os verificadores leem. Repetir a lista aqui
 * criaria duas listas para divergirem. Então esta tabela é indexada pelo
 * CAMINHO, e só acrescenta o que o painel precisa desenhar.
 *
 * `Record<CaminhoConstruido, ...>` é exaustivo de propósito: hoje toda tela
 * construída é seção do menu, e vice-versa (medido). Tela nova sem lugar na
 * navegação deixa de compilar, em vez de nascer invisível — que é como o
 * `/tecnico` ficou um mês fora do menu com a tela no ar.
 *
 * ## Os ícones
 *
 * Todos de Lucide, e todos do vocabulário que o próprio desenho usa. Onde o
 * desenho nomeia a tela, o ícone é o dele; onde ele não chega (os submódulos
 * que entraram depois — telemetria, preditiva, integrações, financeiro,
 * orçamento, investimentos, renovação, qualidade da IA, piloto), o ícone
 * REUSA um conceito já no vocabulário, como a skill `style` manda: reutilizar
 * antes de escolher um novo, e conceito novo é decisão de quem assina o
 * manual, não da tela.
 */

export interface DetalheDaTela {
  /** O título do grupo dentro do painel: "Dia a dia", "Controle", "Campo". */
  readonly grupo: string;
  readonly Icone: ComponentType<{ className?: string }>;
  /** Uma frase dizendo o que a tela responde. É o que o painel escreve embaixo do nome. */
  readonly descricao: string;
}

export const DETALHE_DA_TELA: Record<CaminhoConstruido, DetalheDaTela> = {
  /* ------------------------------------------------------------- Painel */
  '/painel': {
    grupo: 'Painel',
    Icone: LayoutDashboard,
    descricao: 'Máquinas indisponíveis, preventivas vencidas, O.S. abertas e exceções de combustível.',
  },
  '/painel/gestor': {
    grupo: 'Painel',
    Icone: ChartColumn,
    descricao: 'O que o período custou, e onde agir primeiro, com o porquê escrito.',
  },
  '/painel/ia': {
    grupo: 'Inteligência',
    Icone: Sparkles,
    descricao: 'Toda resposta que a IA deu, a nota de quem pediu e o acerto da leitura do papel.',
  },
  '/painel/piloto': {
    grupo: 'Inteligência',
    Icone: ClipboardCheck,
    descricao: 'O grupo do piloto, a qualidade da carga inicial e os indicadores do guia.',
  },

  /* -------------------------------------------------------- Equipamentos */
  '/equipamentos': {
    grupo: 'Frota',
    Icone: Truck,
    descricao: 'Situação da frota agora, por obra e por centro de custo.',
  },
  '/equipamentos/novo': {
    grupo: 'Frota',
    Icone: Plus,
    descricao: 'A ficha da máquina. O que ficar em branco é herdado da configuração.',
  },
  '/equipamentos/catalogo': {
    grupo: 'Catálogo',
    Icone: Layers,
    descricao: 'Fabricantes, modelos e configurações: a hierarquia que evita tratar versões diferentes como iguais.',
  },
  /* `qr-code`: o adesivo da máquina (style, 26/10/2026), aqui o arquivo dele. */
  '/equipamentos/identificacao': {
    grupo: 'Catálogo',
    Icone: QrCode,
    descricao: 'O arquivo dos adesivos de identificação, com o código e os QR, para mandar à gráfica.',
  },
  '/equipamentos/renovacao': {
    grupo: 'Catálogo',
    Icone: CalendarClock,
    descricao: 'Manter, reformar, trocar ou vender: o custo de propriedade de cada máquina, com as premissas à vista.',
  },

  /* ---------------------------------------------------------- Combustível */
  '/combustivel': {
    grupo: 'Dia a dia',
    Icone: LayoutGrid,
    descricao: 'Abastecimento, tanques, ordens, consumo e cobranças da frota, numa tela só.',
  },
  '/combustivel/lancamento': {
    grupo: 'Dia a dia',
    Icone: Fuel,
    descricao: 'O abastecimento de cada máquina, pelo comboio, pelo tanque ou no posto.',
  },
  '/combustivel/lancamento/completo': {
    grupo: 'Dia a dia',
    Icone: ListPlus,
    descricao: 'O formulário inteiro: lubrificante, data retroativa, ordem e as pessoas.',
  },
  '/combustivel/ordens': {
    grupo: 'Dia a dia',
    Icone: ClipboardList,
    descricao: 'A autorização dada antes do abastecimento.',
  },
  '/combustivel/tanques': {
    grupo: 'Controle',
    Icone: Cylinder,
    descricao: 'Saldo de cada tanque, balanço, recebimento e transferência.',
  },
  '/combustivel/consumo': {
    grupo: 'Controle',
    Icone: Droplet,
    descricao: 'Consumo por hora contra a faixa esperada, e as aplicações de lubrificante.',
  },
  '/combustivel/cobrancas': {
    grupo: 'Controle',
    Icone: Receipt,
    descricao: 'A cobrança do posto contra os abastecimentos externos.',
  },
  '/combustivel/folhas': {
    grupo: 'Campo',
    Icone: Printer,
    descricao: 'Emitir, escanear e conferir a folha de papel.',
  },
  '/combustivel/cadastros': {
    grupo: 'Configuração',
    Icone: Settings2,
    descricao: 'Tanque, posto e produto: os cadastros de que o lançamento tira as listas.',
  },

  /* ------------------------------------------------------------ Operação */
  '/operacao': {
    grupo: 'Apontamento',
    Icone: Clock,
    descricao: 'O que cada máquina fez, e por que deixou de trabalhar.',
  },
  '/operacao/folhas': {
    grupo: 'Apontamento',
    Icone: Printer,
    descricao: 'Quem opera preenche o turno; quem escaneia confere o que a IA leu e lança.',
  },
  '/operacao/conciliacao': {
    grupo: 'Conferência',
    Icone: Scale,
    descricao: 'Horímetro, apontamento e hora reconhecida pelo cliente, lado a lado.',
  },
  '/operacao/importacoes': {
    grupo: 'Conferência',
    Icone: Upload,
    descricao: 'O arquivo do cliente passa pela conferência antes de valer.',
  },
  '/operacao/telemetria': {
    grupo: 'Telemetria',
    Icone: Timer,
    descricao: 'Cada dispositivo, a máquina em que está e quando falou pela última vez.',
  },
  '/operacao/telemetria/fila': {
    grupo: 'Telemetria',
    Icone: ListChecks,
    descricao: 'A área de estágio: o que chegou e ainda não entrou, com o que fazer.',
  },
  '/operacao/telemetria/conciliacao': {
    grupo: 'Telemetria',
    Icone: Scale,
    descricao: 'A leitura do aparelho contra a do apontamento, quando as duas existem.',
  },
  '/operacao/telemetria/integracoes': {
    grupo: 'Telemetria',
    Icone: Settings2,
    descricao: 'As integrações de telemetria, com a chave de cada uma guardada fora da tela.',
  },
  '/operacao/cadastros': {
    grupo: 'Configuração',
    Icone: ListChecks,
    descricao: 'O vocabulário das paradas e as horas que cada máquina deveria trabalhar.',
  },

  /* ---------------------------------------------------------- Manutenção */
  '/manutencao': {
    grupo: 'Preventiva',
    Icone: CalendarClock,
    descricao: 'O que venceu, o que vence logo, e em que leitura a previsão se apoia.',
  },
  '/manutencao/preditiva': {
    grupo: 'Preditiva',
    Icone: History,
    descricao: 'Cada corretiva e a classificação dela: componente, modo e categoria da causa.',
  },
  '/manutencao/preditiva/recorrencias': {
    grupo: 'Preditiva',
    Icone: ListChecks,
    descricao: 'A falha que volta no mesmo modelo. Os fatores são associação, não causa.',
  },
  '/manutencao/preditiva/previsoes': {
    grupo: 'Preditiva',
    Icone: ChartColumn,
    descricao: 'As previsões em piloto, só acima da base mínima, e o que falta quando ela não há.',
  },
  '/manutencao/preditiva/propostas': {
    grupo: 'Preditiva',
    Icone: ListPlus,
    descricao: 'A proposta técnica que sai da análise. Quem propõe não decide.',
  },

  /* ----------------------------------------------------- Ordens e inspeção */
  '/ordens': {
    grupo: 'Oficina',
    Icone: ClipboardList,
    descricao: 'O que está em reparo, o que está esperando, e o que impede a liberação.',
  },
  '/inspecao': {
    grupo: 'Inspeção',
    Icone: ClipboardCheck,
    descricao: 'Os achados de campo que ainda não foram tratados.',
  },
  '/inspecao/checklists': {
    grupo: 'Inspeção',
    Icone: ListTodo,
    descricao: 'A lista de verificação de cada modelo ou configuração.',
  },

  /* ------------------------------------------------------------- Estoque */
  '/estoque': {
    grupo: 'Almoxarifado',
    Icone: Package,
    descricao: 'O que há, o que já está prometido a uma O.S., e o que falta repor.',
  },
  '/compras': {
    grupo: 'Compras',
    Icone: ShoppingCart,
    descricao: 'A demanda das preventivas e do mínimo de estoque, descontado o que já está em pedido.',
  },

  /* -------------------------------------------------------------- Custos */
  '/custos': {
    grupo: 'Resultado',
    Icone: CircleDollarSign,
    descricao: 'Combustível, peças, lubrificante e terceiros apropriados à máquina no período.',
  },
  '/receita': {
    grupo: 'Resultado',
    Icone: Receipt,
    descricao: 'As horas reconhecidas do mês, com o valor da regra vigente do contrato.',
  },
  '/financeiro': {
    grupo: 'Financeiro',
    Icone: ChartColumn,
    descricao: 'A DRE gerencial por competência, conferida ao centavo contra Custos e Medições.',
  },
  '/orcamento': {
    grupo: 'Financeiro',
    Icone: Scale,
    descricao: 'O previsto contra o realizado, e o caixa projetado do período.',
  },
  '/investimentos': {
    grupo: 'Financeiro',
    Icone: ListPlus,
    descricao: 'As propostas de investimento, com nota e corte pelo saldo do orçamento.',
  },

  /* ------------------------------------------------------------- Pessoas */
  '/pessoas': {
    grupo: 'Documentos',
    Icone: FileText,
    descricao: 'Por obra, o documento exigido que está ausente, vencido ou sem validade, da máquina e de quem a opera.',
  },

  /* ----------------------------------------------------------- Cadastros */
  '/cadastros': {
    grupo: 'Empresa',
    Icone: Building2,
    descricao: 'Filiais, clientes, obras e centros de custo: a base que todos os módulos compartilham.',
  },
  '/cadastros/governanca': {
    grupo: 'Empresa',
    Icone: ShieldCheck,
    descricao: 'Quem faz o quê, e quem aprova até quanto.',
  },
  '/cadastros/trilha': {
    grupo: 'Empresa',
    Icone: History,
    descricao: 'A trilha de alterações dos cadastros, com quem e quando.',
  },
  '/cadastros/integracoes': {
    grupo: 'Integrações',
    Icone: Settings2,
    descricao: 'A matriz de sistema mestre: quem escreve o quê, e o que só entra de fora.',
  },
  '/cadastros/integracoes/lotes': {
    grupo: 'Integrações',
    Icone: Upload,
    descricao: 'Cada lote trocado com o ERP, linha a linha, com o erro de cada uma.',
  },
  '/cadastros/integracoes/titulos': {
    grupo: 'Integrações',
    Icone: Receipt,
    descricao: 'Títulos a pagar e a receber vindos do ERP, e as baixas de cada um.',
  },
  '/cadastros/integracoes/correspondencias': {
    grupo: 'Integrações',
    Icone: ListChecks,
    descricao: 'O de-para entre o código daqui e o do ERP, por entidade.',
  },
  '/cadastros/integracoes/divergencias': {
    grupo: 'Integrações',
    Icone: Scale,
    descricao: 'O que o ERP devolveu diferente, esperando quem resolva.',
  },

  /* ---------------------------------------------------------- Biblioteca */
  '/tecnico': {
    grupo: 'Técnico',
    Icone: BookOpen,
    descricao: 'Manual, catálogo, boletim e procedimento, ligados ao equipamento a que se aplicam.',
  },
};

/**
 * O NOME CURTO DO MÓDULO, só para o trilho.
 *
 * O trilho tem 96 px, e o nome vai embaixo do ícone. "Pessoas e documentos"
 * não cabe em duas linhas nessa largura sem cortar, e o desenho do dono usa a
 * palavra curta: "Pessoas", "Cadastros", "Ordens".
 *
 * O nome INTEIRO não se perde: ele é o título do painel, está no `title` do
 * botão e é o que a trilha da barra superior escreve. O trilho é onde o nome
 * compete por espaço com o ícone; os outros três lugares não têm esse aperto.
 *
 * Módulo que não está aqui usa o nome inteiro, que é o caso da maioria.
 */
export const ROTULO_CURTO: Partial<Record<IdModulo, string>> = {
  relatorios: 'Painel',
  ordens: 'Ordens',
  operadores: 'Pessoas',
  obras: 'Cadastros',
  biblioteca: 'Biblioteca',
};

/**
 * A linha que o painel escreve embaixo do nome do módulo, no topo.
 *
 * É o resumo do que o módulo inteiro faz — o desenho a chama de `sub`. Ela
 * responde "cheguei no lugar certo?" antes de a pessoa ler a lista de telas.
 */
export const RESUMO_DO_MODULO: Record<IdModulo, string> = {
  relatorios: 'Visão geral, favoritos e recentes',
  equipamentos: 'Frota, ficha de cada máquina e catálogo',
  combustivel: 'Abastecimento, tanques, consumo e cobranças',
  horimetro: 'Turnos, horas, paradas e conciliação',
  manutencao: 'Planos preventivos e manutenção preditiva',
  ordens: 'Ordens de serviço, inspeção e ocorrências',
  pecas: 'Saldos, reservas e compras',
  custos: 'Custo por máquina, receita e resultado',
  operadores: 'Documentos e impedimentos na obra',
  obras: 'Estrutura, perfis, auditoria e integrações',
  biblioteca: 'Manuais, catálogos e documentos técnicos',
};
